import { useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, DialogContent, FormHelperText, Typography } from "@mui/material"
import { faClock, faTruck } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { MAX_ORDER_COMMENT_LENGTH } from "../../../../../../constants/inputValidationDefaults"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { FormikTextField } from "../../../../forms/Formik"
import { StandardDialog } from "../../../../dialogs/StandardDialog"

import type { TakeawayDeliveryLocation } from "../takeawayTypes"
import type { CheckoutFormValues } from "./takeawayCheckoutLib"
import { TakeawayCheckoutSectionHeader } from "./TakeawayCheckoutSectionHeader"

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
    },
    locationList: {
        display: "flex",
        flexDirection: "column",
    },
    locationCard: {
        padding: "1rem",
        cursor: "pointer",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    locationCardSelected: {
        background: theme.palette.tailwind.blue[50],
    },
    locationName: {
        fontWeight: 700,
        marginBottom: ".25rem",
    },
    timeRow: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
        marginBottom: ".75rem",
    },
    timeIcon: {
        color: theme.palette.tailwind.gray[400],
    },
    descriptionRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: ".5rem",
    },
    description: {
        flex: 1,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    },
    showMoreLink: {
        flexShrink: 0,
        whiteSpace: "nowrap",
        fontSize: ".875rem",
        color: theme.palette.text.secondary,
        textDecoration: "underline",
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
    },
    commentSection: {
        marginTop: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    commentLabel: {
        fontWeight: 700,
    },
}))

type LocationCardProps = Readonly<{
    location: TakeawayDeliveryLocation
    isSelected: boolean
    onSelect: (id: number) => void
}>

function LocationCard({ location, isSelected, onSelect }: LocationCardProps) {
    const { classes, cx } = useStyles()
    const dt = useDateTime()
    const [descriptionOpen, setDescriptionOpen] = useState(false)

    return (
        <>
            <Box
                className={cx(classes.locationCard, { [classes.locationCardSelected]: isSelected })}
                onClick={() => onSelect(location.id)}
            >
                <Typography variant="body1" className={classes.locationName}>
                    {location.displayName}
                </Typography>

                {location.timeInterval &&
                    (() => {
                        const formatted = dt.formatTimeInterval(location.timeInterval)
                        if (!formatted) return null
                        const { start, end } = formatted
                        return (
                            <Box className={classes.timeRow}>
                                <FontAwesomeIcon icon={faClock} className={classes.timeIcon} />
                                <LocalizedStrict id="takeaway-checkout-delivery-location-pickup-label">
                                    <Typography variant="body2">Pick up:</Typography>
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id="takeaway-checkout-delivery-location-pickup-time"
                                    vars={{ start, end }}
                                >
                                    <Typography variant="body2" fontWeight={700}>
                                        {start} - {end}
                                    </Typography>
                                </LocalizedStrict>
                            </Box>
                        )
                    })()}

                {location.description && (
                    <Box className={classes.descriptionRow}>
                        <Typography variant="body2" color="text.secondary" className={classes.description}>
                            {location.description}
                        </Typography>
                        <LocalizedStrict id="takeaway-checkout-delivery-location-show-more-button">
                            <button
                                className={classes.showMoreLink}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setDescriptionOpen(true)
                                }}
                            >
                                Show more
                            </button>
                        </LocalizedStrict>
                    </Box>
                )}
            </Box>

            {descriptionOpen && location.description && (
                <StandardDialog
                    open
                    dialogTitle={location.displayName}
                    onClose={() => setDescriptionOpen(false)}
                    scroll="body"
                >
                    <DialogContent>
                        <Typography variant="body1">{location.description}</Typography>
                    </DialogContent>
                </StandardDialog>
            )}
        </>
    )
}

type TakeawayDeliverySectionProps = Readonly<{
    locations: TakeawayDeliveryLocation[]
    selectedLocationId: number | null
    onLocationSelect: (id: number) => void
}>

export function TakeawayDeliverySection({
    locations,
    selectedLocationId,
    onLocationSelect,
}: TakeawayDeliverySectionProps) {
    const { classes } = useStyles()
    const { errors, touched, submitCount, values } = useFormikContext<CheckoutFormValues>()
    const error = touched.deliveryLocationId || submitCount > 0 ? errors.deliveryLocationId : undefined
    const charsRemaining = MAX_ORDER_COMMENT_LENGTH - (values.orderComment?.length ?? 0)

    return (
        <Box className={classes.root}>
            <TakeawayCheckoutSectionHeader
                icon={faTruck}
                title={
                    <LocalizedStrict id="takeaway-confirm-order-delivery-section-title">
                        <span>Delivery</span>
                    </LocalizedStrict>
                }
            />

            {locations.length > 0 && (
                <Box className={classes.locationList}>
                    {locations.map((location) => (
                        <LocationCard
                            key={location.id}
                            location={location}
                            isSelected={selectedLocationId === location.id}
                            onSelect={onLocationSelect}
                        />
                    ))}
                </Box>
            )}

            {error && <FormHelperText error>{error}</FormHelperText>}

            <Box className={classes.commentSection}>
                <LocalizedStrict id="takeaway-confirm-order-comment-title">
                    <Typography variant="body1" className={classes.commentLabel}>
                        Order comment:
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict
                    id="takeaway-confirm-order-comment-placeholder"
                    attrs={{ label: true, placeholder: false, helperText: true }}
                    vars={{ charsRemaining }}
                >
                    <FormikTextField<CheckoutFormValues>
                        name="orderComment"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        inputProps={{ maxLength: MAX_ORDER_COMMENT_LENGTH }}
                        label="Write a comment (optional)"
                        helperText={`Remaining characters: ${charsRemaining}`}
                    />
                </LocalizedStrict>
            </Box>
        </Box>
    )
}
